import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import isEqual from 'lodash.isequal';
import orderBy from 'lodash.orderby';
import { EVISIT_STATUS } from '../../../config/constants';
import TabContent from './TabContent';
import { Button, withParams } from '../../../components';
import {
	loadProviders,
	createTextaVisit,
	isNumberMobile,
	getOpenVisits,
} from './api';
import { checkNotificationPromise } from '../../../lib/util';
import TextaVisitModal from './TextaVisitModal';

class TrackingBoard extends React.Component {
	static propTypes = {
		user: PropTypes.object,
		params: PropTypes.object,
		showNotification: PropTypes.func.isRequired,
	};

	constructor(props) {
		super(props);
		this.state = {
			page: props.params?.page || 'practice-patients',
			openVisits: [],
			scheduledVisits: [],
			isInstavisitModalOpen: false,
			providers: [],
			schedVisitOrder: 'asc',
			notificationsAllowed: window?.Notification?.permission === 'granted',
			canAskAboutNotifications: window?.Notification?.permission !== 'denied',
		};

		this.props.user.on(
			'change:trackingBoardData',
			this.handleShowNotifications
		);
	}

	componentDidMount() {
		this.loadData();
	}

	componentWillUnmount() {
		this.props.user.off(
			'change:trackingBoardData',
			this.handleShowNotifications
		);
	}

	handleShowNotifications = () => {
		const { user } = this.props;
		const { openVisits, scheduledVisits, notificationsAllowed } = this.state;
		const trackingBoardData = user.get('trackingBoardData');
		const openVisitIds = trackingBoardData
			.filter(
				(visit) =>
					![EVISIT_STATUS.SCHEDULED, EVISIT_STATUS.SCHEDULED_PENDING].includes(
						visit.evisit_status
					)
			)
			.map((visit) => visit.id);
		const scheduledVisitIds = trackingBoardData
			.filter((visit) =>
				[EVISIT_STATUS.SCHEDULED, EVISIT_STATUS.SCHEDULED_PENDING].includes(
					visit.evisit_status
				)
			)
			.map((visit) => visit.id);

		// show notifications if id list is different from visits shown
		const currentOpenIds = openVisits.map(({ id }) => id);
		const currentScheduledIds = scheduledVisits.map(({ id }) => id);
		const newOpenIds = openVisitIds.filter(
			(id) => !currentOpenIds.includes(id)
		);
		const newScheduledIds = scheduledVisitIds.filter(
			(id) => !currentScheduledIds.includes(id)
		);

		if (notificationsAllowed) {
			if (newOpenIds.length > 0) {
				const n = new Notification('Care Convene', {
					body: `You have ${newOpenIds.length} new visit(s)`,
					tag: 'openVisits',
					// requireInteraction: true,
				});
				n.onclick = () => {
					window.location = '#trackingboard/practice-patients';
					window.focus();
					n.close();
				};
			}
			if (newScheduledIds.length > 0) {
				const n = new Notification('Care Convene', {
					body: `You have ${newScheduledIds.length} new scheduled appointment(s)`,
					tag: 'scheduledVisits',
				});
				n.onclick = () => {
					window.location = '#trackingboard/scheduled-visits';
					window.focus();
					n.close();
				};
			}
		}

		if (
			!isEqual(currentOpenIds.sort(), newOpenIds.sort()) ||
			!isEqual(currentScheduledIds.sort(), newScheduledIds.sort())
		) {
			this.loadData();
		}
	};

	loadData = () => {
		getOpenVisits(this.props.user.get('provider_practice_id')).then(
			({ data }) => {
				data.forEach((visit) => {
					if (visit.evisit_provider_transactions) {
						if (visit.evisit_provider_transactions.length > 0) {
							visit.currentProviderId =
								visit.evisit_provider_transactions[0].providerId;
							if (visit.evisit_provider_transactions[0].provider) {
								visit.currentProvider =
									visit.evisit_provider_transactions[0].provider;
							}
						} else {
							visit.currentProviderId = visit.providerId;
							if (visit.provider) {
								visit.currentProvider = visit.provider;
							}
						}
					}
				});
				const openVisits = data.filter(
					(visit) =>
						![
							EVISIT_STATUS.SCHEDULED,
							EVISIT_STATUS.SCHEDULED_PENDING,
						].includes(visit.evisit_status)
				);
				const scheduledVisits = data.filter((visit) =>
					[EVISIT_STATUS.SCHEDULED, EVISIT_STATUS.SCHEDULED_PENDING].includes(
						visit.evisit_status
					)
				);
				this.setState({ openVisits, scheduledVisits });
			}
		);
	};

	handleSwitchToMyPatients = (event) => {
		event.preventDefault();
		const page = 'practice-patients';
		window.location = `#/trackingboard/${page}`;
	};

	handleSwitchToScheduledVisits = (event) => {
		event.preventDefault();
		const page = 'scheduled-visits';
		window.location = `#/trackingboard/${page}`;
	};

	handleRowClick = (visit) => {
		window.location = `#evisit/${visit.id}`;
	};

	handleOpenInstavisitModal = async () => {
		const { data } = await loadProviders(this.props.user.get('id'));
		this.setState({ isInstavisitModalOpen: true, providers: data.status });
	};

	handleCloseInstavisitModal = () => {
		this.setState({ isInstavisitModalOpen: false });
	};

	handleCreateInstavisit = async (visitData) => {
		try {
			const { data } = await createTextaVisit(
				this.props.user.get('id'),
				visitData
			);
			this.setState({ isInstavisitModalOpen: false });
			if (data && data.cb === 'visit created') {
				this.props.showNotification({
					status: 'success',
					message: 'Text-a-Visit created. Patient has been notifiied.',
				});
			} else {
				this.props.showNotification({
					message: 'Unable to create Text-a-Visit!',
					status: 'danger',
					timeout: 10000,
				});
			}
		} catch (err) {
			this.props.showNotification({
				message: 'Unable to create Text-a-Visit!',
				status: 'danger',
				timeout: 10000,
			});
		}
	};

	handleEnableBrowserNotifications = () => {
		if (checkNotificationPromise()) {
			Notification.requestPermission().then(this.handleNotificationResult);
		} else {
			Notification.requestPermission(this.handleNotificationResult);
		}
	};

	handleNotificationResult = (permission) => {
		this.setState({
			notificationsAllowed: permission === 'granted',
			canAskAboutNotifications: permission !== 'denied',
		});
	};

	handleVerifyMobileNumber = async (number) => {
		const userId = this.props.user.get('id');
		const phoneData = await isNumberMobile(userId, number);
		this.setState({ phoneData });
	};

	handleChangeSchedVisitSort = () => {
		this.setState((currState) => ({
			schedVisitOrder: currState.schedVisitOrder === 'asc' ? 'desc' : 'asc',
		}));
	};

	render() {
		const {
			page,
			openVisits,
			scheduledVisits,
			isInstavisitModalOpen,
			providers,
			phoneData,
			schedVisitOrder,
			canAskAboutNotifications,
			notificationsAllowed,
		} = this.state;
		const { user } = this.props;
		let userObj = {};
		if (user.toJSON) {
			userObj = user.toJSON();
		}

		const total = openVisits.length;

		const showTextAVisit = !!userObj.provider_practice.text_visit_ind;
		const { qualified_health_professional_ind, id } = userObj;
		const noPolling = !!userObj.provider_practice.no_polling_ind;

		return (
			<div className="content-wrapper dashboard">
				<div className="clearfix">
					{canAskAboutNotifications && !notificationsAllowed && (
						<Button
							title="Enable Browser Notifications"
							primary
							onClick={this.handleEnableBrowserNotifications}
							className="mb"
						/>
					)}
					<div className="pull-right">
						{noPolling && (
							<Button
								icon="refresh"
								altTitle="Refresh Tracking Board"
								onClick={this.loadData}
								className="mb"
							/>
						)}
						{showTextAVisit && (
							<React.Fragment>
								<Button
									title="Start Text-a-Visit™"
									primary
									onClick={this.handleOpenInstavisitModal}
									className="mb ml"
								/>
								<TextaVisitModal
									isOpen={isInstavisitModalOpen}
									onCancel={this.handleCloseInstavisitModal}
									providers={providers}
									currentUser={qualified_health_professional_ind ? `${id}` : ''}
									onSubmit={this.handleCreateInstavisit}
									onVerifyMobileNumber={this.handleVerifyMobileNumber}
									phoneData={phoneData}
								/>
							</React.Fragment>
						)}
					</div>
				</div>
				<div role="tabpanel" className="panel">
					<ul role="tablist" className="nav nav-tabs nav-justified">
						<li
							role="presentation"
							className={cn({ active: page === 'practice-patients' })}
						>
							<a
								href="#"
								aria-controls="practice-patients"
								role="tab"
								data-toggle="tab"
								onClick={this.handleSwitchToMyPatients}
							>
								My Patients <div className="label label-info">{total}</div>
							</a>
						</li>
						<li
							role="presentation"
							className={cn({ active: page === 'scheduled-visits' })}
						>
							<a
								href="#"
								aria-controls="scheduled-visits"
								role="tab"
								data-toggle="tab"
								onClick={this.handleSwitchToScheduledVisits}
							>
								Scheduled Visits{' '}
								<div className="label label-info">{scheduledVisits.length}</div>
							</a>
						</li>
					</ul>
					<div className="tab-content p0">
						<TabContent
							id="practice-patients"
							selected={page === 'practice-patients'}
							visits={openVisits}
							onRowClick={this.handleRowClick}
							user={userObj}
						/>
						<TabContent
							id="scheduled-visits"
							selected={page === 'scheduled-visits'}
							visits={orderBy(
								scheduledVisits,
								[
									(visit) =>
										visit.appointment_schedule &&
										visit.appointment_schedule.scheduled_start_dttm,
								],
								schedVisitOrder
							)}
							onRowClick={this.handleRowClick}
							user={userObj}
							onChangeOrder={this.handleChangeSchedVisitSort}
							sortOrder={schedVisitOrder}
						/>
					</div>
				</div>
			</div>
		);
	}
}

export default withParams(TrackingBoard);
